<script>
  import { onMount, tick } from 'svelte';
  import NavBarReusable from './NavbarReusable.svelte';
  import { actionDetailRows, defaultClassTimes, oneHourAfter, buildMutationPayload, buildTimedRange, calendarApi, calendarDateKey, calendarTime, calendarsNeedingValidation, classTotal, filterAndSortCalendars, filterStudents, generatedClassTitle, mergeCalendarHealth, monthGrid, parseRecurrence, stableCalendarColor, unavailableTimeRanges, viewDateRange, weekdayLabel, weekDays } from './adminCalendar.js';

  let loading = true;
  let error = '';
  let options = { calendars: [], students: [] };
  let events = [];
  let eventsLoading = false;
  let eventRequestNumber = 0;
  let actions = [];
  let selection = { kind: 'none' };
  let calendarSearch = '';
  let studentCalendarSearch = '';
  let hiddenListOpen = false;
  let visibilityPendingTeacherIds = [];
  let view = 'month';
  let todayKey = calendarDateKey(new Date());
  let focusDate = new Date(`${todayKey}T12:00:00Z`);
  let dialogOpen = false;
  let dialogDirty = false;
  let editingEvent = null;
  let actionHistoryOpen = false;
  let expandedActionId = null;
  let actionDetails = {};
  let actionDetailLoadingId = null;
  let actionDetailError = null;
  let submitting = false;
  let mutationNotice = '';
  let form = emptyForm();
  let titleInput;
  let studentSearchInput;
  let studentSearch = '';
  let studentPickerExpanded = true;
  let returnFocus;

  $: visibleDays = view === 'month' ? monthGrid(focusDate) : weekDays(focusDate);
  $: selectedCalendarIds = selection.kind === 'teachers' ? selection.ids : [];
  $: selectedCalendars = options.calendars.filter((calendar) => selectedCalendarIds.includes(calendar.id));
  $: filteredCalendars = filterAndSortCalendars(options.calendars, calendarSearch);
  $: visibleCalendars = filteredCalendars.filter((calendar) => !calendar.hidden);
  $: hiddenCalendars = filteredCalendars.filter((calendar) => calendar.hidden);
  $: sidebarStudents = filterStudents(options.students, studentCalendarSearch);
  $: displayedClassTotal = classTotal(events, visibleDays);
  $: selectedStudents = form.students.filter((student) => form.selectedStudentIds.includes(student.id));
  $: selectedSubject = (options.subjects || []).find((subject) => subject.name === form.subject);
  $: generatedTitle = generatedClassTitle(selectedSubject, selectedStudents, form.groupLetter);
  $: availableStudents = filterStudents(
    form.students.filter((student) => !form.selectedStudentIds.includes(student.id)),
    studentSearch,
  );
  $: notificationChoices = buildNotificationChoices(form.calendar_id, selectedStudents);
  $: unavailableTimes = unavailableTimeRanges(events, form.calendar_id, form.startDate, editingEvent?.id);

  function emptyForm(now = new Date()) {
    return {
      calendar_id: '', subject: '', title: '', groupLetter: 'A', description: '', selectedStudentIds: [], students: [], ...defaultClassTimes(now),
       recurring: false, frequency: 'weekly', interval: 1,
      weekdays: [], scope: 'single', reason: '', notifications: [], confirm_reset_exceptions: false,
      responsibilityEnabled: false, responsibleParty: 'teacher', incidentType: 'other', expected_etag: '',
    };
  }

  function authFailure(message) {
    error = message === 'Authentication credentials were not provided.' ? 'Please sign in with a staff account.' : message;
  }

  async function initialise() {
    loading = true;
    try {
      [options, { actions }] = await Promise.all([calendarApi.options(), calendarApi.actions()]);
      selection = { kind: 'none' };
      await loadEvents();
      validateCalendars();
    } catch (caught) { authFailure(caught.message); }
    loading = false;
  }

  async function validateCalendars() {
    const ids = calendarsNeedingValidation(options.calendars);
    for (let offset = 0; offset < ids.length; offset += 10) {
      try {
        const result = await calendarApi.health(ids.slice(offset, offset + 10));
        options = { ...options, calendars: mergeCalendarHealth(options.calendars, result.calendars) };
      } catch (caught) {
        error = `Calendar validation paused: ${caught.message}`;
        return;
      }
    }
  }

  async function loadEvents() {
    const requestNumber = ++eventRequestNumber;
    events = [];
    if (selection.kind === 'none') { eventsLoading = false; return; }
    eventsLoading = true;
    const requestedSelection = selection;
    const { dateStart, dateEnd } = viewDateRange(focusDate, view);
    try {
      const result = await calendarApi.eventsInView(dateStart, dateEnd, requestedSelection);
      if (requestNumber === eventRequestNumber) events = result.events || [];
    } catch (caught) {
      if (requestNumber === eventRequestNumber) error = caught.message;
    } finally {
      if (requestNumber === eventRequestNumber) eventsLoading = false;
    }
  }

  function toggleCalendar(id) {
    error = '';
    selection = selection.kind === 'teachers' && selection.ids.length === 1 && selection.ids[0] === id
      ? { kind: 'none' }
      : { kind: 'teachers', ids: [id] };
    loadEvents();
  }

  function toggleSidebarStudent(id) {
    error = '';
    selection = selection.kind === 'student' && selection.id === id
      ? { kind: 'none' }
      : { kind: 'student', id };
    loadEvents();
  }

  function selectAll() {
    error = '';
    const ids = options.calendars.filter((calendar) => calendar.writable && !calendar.hidden).map((calendar) => calendar.id);
    selection = ids.length ? { kind: 'teachers', ids } : { kind: 'none' };
    loadEvents();
  }

  function deselectAll() {
    error = '';
    selection = { kind: 'none' };
    loadEvents();
  }

  async function setTeacherVisibility(calendar, hidden) {
    if (visibilityPendingTeacherIds.includes(calendar.teacher_id)) return;
    visibilityPendingTeacherIds = [...visibilityPendingTeacherIds, calendar.teacher_id];
    error = '';
    try {
      const result = await calendarApi.setTeacherVisibility(calendar.teacher_id, hidden);
      options = {
        ...options,
        calendars: options.calendars.map((item) => item.teacher_id === calendar.teacher_id
          ? { ...item, hidden: result.hidden }
          : item),
      };
      if (hidden && selection.kind === 'teachers') {
        const visibleIds = new Set(options.calendars.filter((item) => !item.hidden).map((item) => item.id));
        const ids = selection.ids.filter((id) => visibleIds.has(id));
        if (ids.length !== selection.ids.length) {
          selection = ids.length ? { kind: 'teachers', ids } : { kind: 'none' };
          await loadEvents();
        }
      }
    } catch (caught) {
      error = `Could not update teacher visibility: ${caught.message}`;
    } finally {
      visibilityPendingTeacherIds = visibilityPendingTeacherIds.filter((id) => id !== calendar.teacher_id);
    }
  }

  function move(amount) {
    const next = new Date(focusDate);
    if (view === 'month') focusDate = new Date(Date.UTC(next.getUTCFullYear(), next.getUTCMonth() + amount, 15, 12));
    else { next.setUTCDate(next.getUTCDate() + (amount * 7)); focusDate = next; }
    loadEvents();
  }

  function eventsFor(loadedEvents, day) {
    return loadedEvents.filter((event) => calendarDateKey(event.start) === day.key);
  }

  function openCreate(day) {
    if (!selectedCalendarIds.length) { error = 'Select at least one writable teacher calendar first.'; return; }
    error = '';
    editingEvent = null;
    returnFocus = document.activeElement;
    const now = new Date();
    form = emptyForm(now);
    form.calendar_id = selectedCalendarIds.length === 1 ? selectedCalendarIds[0] : '';
    form.startDate = day.key === calendarDateKey(now) ? form.startDate : day.key;
    form.students = options.students.map((student) => ({ ...student, basePrice: '' }));
    studentSearch = '';
    studentPickerExpanded = true;
    dialogOpen = true;
    dialogDirty = false;
    tick().then(() => studentSearchInput?.focus());
  }

  function openCreateForDay(day) {
    if (selectedCalendarIds.length !== 1) {
      error = 'Select exactly one teacher calendar to create a class by clicking a day.';
      return;
    }
    openCreate(day);
  }

  function openEdit(event) {
    error = '';
    returnFocus = document.activeElement;
    editingEvent = event;
    const start = new Date(event.start);
    const end = new Date(event.end);
    form = emptyForm();
    form.calendar_id = event.calendar_id;
    form.title = event.display_title;
    form.subject = event.subject || '';
    form.groupLetter = event.group_letter || (options.next_group_letters?.[form.subject] || 'A');
    form.description = event.description;
    form.startDate = calendarDateKey(start);
    form.startTime = calendarTime(start);
    form.endTime = calendarTime(end);
    form.selectedStudentIds = event.students.map((student) => student.id);
    form.students = options.students.map((student) => {
      const enrolled = event.students.find((value) => value.id === student.id);
      const defaultPrice = event.students.length >= 2 ? student.default_group_base_price : student.default_individual_base_price;
      return { ...student, basePrice: enrolled ? (enrolled.base_price || defaultPrice) : '' };
    });
    studentSearch = '';
    studentPickerExpanded = false;
    form.recurring = !!event.recurring_event_id;
    form.expected_etag = event.etag || '';
    if (form.recurring) {
      const recurrence = parseRecurrence(event.recurrence);
      form.frequency = recurrence.frequency;
      form.interval = recurrence.interval;
      form.weekdays = recurrence.weekdays;
    }
    dialogOpen = true;
    dialogDirty = false;
    tick().then(() => titleInput?.focus());
  }

  function closeDialog() { dialogOpen = false; dialogDirty = false; tick().then(() => returnFocus?.focus?.()); }

  async function handleMutationConflict(caught) {
    if (caught.code !== 'stale_event') return false;
    const currentEvent = caught.data?.current_event;
    if (currentEvent) {
      openEdit(currentEvent);
      error = 'This class changed in Google Calendar. The latest version has been loaded for review.';
    } else {
      closeDialog();
      await loadEvents();
      error = 'This class no longer exists in Google Calendar.';
    }
    return true;
  }

  function markDirty() { dialogDirty = true; }
  function toggleStudent(id) {
    const adding = !form.selectedStudentIds.includes(id);
    form.selectedStudentIds = form.selectedStudentIds.includes(id)
      ? form.selectedStudentIds.filter((value) => value !== id)
      : [...form.selectedStudentIds, id];
    const group = form.selectedStudentIds.length >= 2;
    form.students = form.students.map((student) => ({
      ...student,
      basePrice: form.selectedStudentIds.includes(student.id) && !student.basePrice
        ? (group ? student.default_group_base_price : student.default_individual_base_price)
        : student.basePrice,
    }));
    form.description = form.students.filter((student) => form.selectedStudentIds.includes(student.id)).map((student) => student.token).join(' / ');
    if (adding) {
      studentSearch = '';
      studentPickerExpanded = false;
    }
    if (form.selectedStudentIds.length >= 2 && !editingEvent) {
      form.groupLetter = options.next_group_letters?.[form.subject] || 'A';
    }
    markDirty();
  }
  function syncDescription() {
    const tokens = form.description.split('/').map((value) => value.trim().toLowerCase()).filter(Boolean);
    form.selectedStudentIds = form.students.filter((student) => tokens.includes(student.token.toLowerCase())).map((student) => student.id);
    markDirty();
  }
  function toggleNotification(choice) {
    const exists = selectedNotification(choice);
    form.notifications = exists
      ? form.notifications.filter((item) => !sameNotification(item, choice))
      : [...form.notifications, { kind: choice.kind, participant_id: choice.participant_id, email: choice.email }];
    markDirty();
  }
  function sameNotification(left, right) { return left.kind === right.kind && Number(left.participant_id) === Number(right.participant_id); }
  function selectedNotification(choice) { return form.notifications.find((item) => sameNotification(item, choice)); }
  function updateNotificationEmail(choice, email) {
    form.notifications = form.notifications.map((item) => sameNotification(item, choice) ? { ...item, email } : item);
    markDirty();
  }
  function buildNotificationChoices(calendarId, students) {
    const calendar = options.calendars.find((item) => item.id === calendarId);
    const teacher = (calendar?.email_destinations || []).map((item) => ({ ...item, participant_id: calendar.teacher_user_id }));
    const studentChoices = students.flatMap((student) => student.email_destinations.map((item) => ({ ...item, participant_id: student.id, label: `${student.name} — ${item.label}` })));
    return [...teacher, ...studentChoices];
  }
  function uuid() { return globalThis.crypto?.randomUUID?.() || `${Date.now()}-0000-4000-8000-${Math.random().toString(16).slice(2).padEnd(12, '0').slice(0, 12)}`; }

  function submittedPayload() {
    const range = buildTimedRange(form.startDate, form.startTime, form.endTime);
    const start = new Date(range.start).toISOString();
    const end = new Date(range.end).toISOString();
    return buildMutationPayload({
      ...form, title: generatedTitle, idempotency_key: uuid(), start, end,
      recurrence: { enabled: form.recurring, frequency: form.frequency, interval: Number(form.interval), weekdays: form.weekdays },
      responsibility: form.responsibilityEnabled ? {
        enabled: true, responsible_party: form.responsibleParty, incident_type: form.incidentType, comment: form.reason,
      } : { enabled: false },
    });
  }
  async function submit() {
    if (submitting) return;
    error = '';
    if (!form.calendar_id || !form.subject || !generatedTitle || !form.selectedStudentIds.length) { error = 'Teacher calendar, subject and at least one student are required.'; return; }
    if (form.scope === 'following' && !form.confirm_reset_exceptions) { error = 'Confirm that future exceptions may be reset.'; return; }
    submitting = true;
    try {
      const payload = submittedPayload();
      const result = editingEvent ? await calendarApi.update(editingEvent.id, payload) : await calendarApi.create(payload);
      mutationNotice = result.status === 'reconciling'
        ? 'Google was updated, but the local copy needs reconciliation. Use Previous actions to retry safely.'
        : 'Calendar change committed.';
      await afterMutation();
    } catch (caught) {
      if (!await handleMutationConflict(caught)) {
        error = caught.code === 'class_conflict' ? 'This class conflicts with another active Tutory class.' : caught.message;
      }
    }
    submitting = false;
  }

  async function cancelClass() {
    if (!editingEvent || !window.confirm('Delete this class using the selected recurrence scope?')) return;
    error = '';
    submitting = true;
    try {
      const payload = submittedPayload();
      const result = await calendarApi.cancel(editingEvent.id, payload);
      mutationNotice = result.status === 'reconciling'
        ? 'Google was updated, but the local copy needs reconciliation. Use Previous actions to retry safely.'
        : 'Class cancelled.';
      await afterMutation();
    } catch (caught) {
      if (!await handleMutationConflict(caught)) {
        error = caught.code === 'google_delete_failed'
          ? 'Google Calendar could not delete this class. Please try again.'
          : caught.message;
      }
    }
    submitting = false;
  }

  async function afterMutation() {
    closeDialog();
    await Promise.all([loadEvents(), refreshActions()]);
  }
  async function refreshActions() {
    ({ actions } = await calendarApi.actions());
    actionDetails = {};
    expandedActionId = null;
  }
  async function retry(actionId) { await calendarApi.retry(actionId); await refreshActions(); }
  async function reconcile(actionId) { await calendarApi.reconcile(actionId); await Promise.all([refreshActions(), loadEvents()]); }
  async function toggleActionDetails(actionId) {
    if (expandedActionId === actionId) { expandedActionId = null; return; }
    expandedActionId = actionId;
    actionDetailError = null;
    if (actionDetails[actionId]) return;
    actionDetailLoadingId = actionId;
    try {
      const detail = await calendarApi.action(actionId);
      actionDetails = { ...actionDetails, [actionId]: detail };
    } catch (caught) {
      actionDetailError = { id: actionId, message: caught.message };
    } finally {
      if (actionDetailLoadingId === actionId) actionDetailLoadingId = null;
    }
  }
  function actionCalendarName(calendarId) {
    return options.calendars.find((calendar) => calendar.id === calendarId)?.name || calendarId;
  }
  function incidentLabel(responsibility) {
    const party = responsibility.responsible_party === 'teacher' ? 'Teacher' : responsibility.responsible_party === 'student' ? 'Student' : 'Class';
    const type = responsibility.incident_type === 'late' ? 'Late' : responsibility.incident_type === 'cancellation' ? 'Cancellation' : 'Other';
    return `${party} · ${type}`;
  }

  onMount(() => {
    initialise();
    const poll = setInterval(() => {
      todayKey = calendarDateKey(new Date());
      if (!dialogDirty) loadEvents();
    }, 60000);
    return () => clearInterval(poll);
  });
</script>

<svelte:window on:keydown={(event) => dialogOpen && event.key === 'Escape' && closeDialog()} />
<div class="calendar-page">
  <NavBarReusable label="Tutory Calendar" backLink="/admin/all" />
  <div class="workspace">
    <aside>
      <button class="create" on:click={() => openCreate({ key: todayKey })}>＋ Create class</button>
      <section class="student-calendars">
        <div class="aside-heading"><strong>Student calendars</strong></div>
        <input class="calendar-search" type="search" aria-label="Search student calendars" placeholder="Search students" bind:value={studentCalendarSearch} />
        <div class="student-calendar-list">
          {#each sidebarStudents as student}
            <label class="calendar-choice-main student-calendar-choice">
              <input type="checkbox" checked={selection.kind === 'student' && selection.id === student.id} on:change={() => toggleSidebarStudent(student.id)} />
              <i style={`background:${stableCalendarColor(`student-${student.id}`)}`}></i><span>{student.name}</span>
            </label>
          {:else}<p class="no-calendars">No matching students.</p>{/each}
        </div>
      </section>
      <div class="aside-heading"><strong>Teacher calendars</strong><div class="calendar-selection-actions"><button type="button" on:click={selectAll}>Select all</button><button type="button" on:click={deselectAll}>Deselect all</button></div></div>
      <input class="calendar-search" type="search" aria-label="Search teacher calendars" placeholder="Search calendars" bind:value={calendarSearch} />
      {#each visibleCalendars as calendar}
        <div class="calendar-choice">
          <label class:disabled={!calendar.writable} class="calendar-choice-main">
            <input type="checkbox" disabled={!calendar.writable} checked={selectedCalendarIds.includes(calendar.id)} on:change={() => toggleCalendar(calendar.id)} />
            <i style={`background:${stableCalendarColor(calendar.id)}`}></i><span>{calendar.name}</span>
            {#if calendar.error_code === 'google_unavailable' && calendar.writable}
              <small>Validation temporarily unavailable; using the last successful check</small>
            {:else if calendar.status !== 'ready'}
              <small>{calendar.status === 'no_classes' ? 'No classes' : calendar.status === 'checking' ? 'Checking…' : calendar.status === 'unavailable' ? 'Validation temporarily unavailable' : calendar.status.replace('_', ' ')}</small>
            {/if}
          </label>
          <button type="button" class="visibility-toggle" aria-label={`Hide ${calendar.name}`} title="Move to hidden calendars" disabled={visibilityPendingTeacherIds.includes(calendar.teacher_id)} on:click={() => setTeacherVisibility(calendar, true)}><i class="fa-regular fa-eye" aria-hidden="true"></i></button>
        </div>
      {:else}<p class="no-calendars">No matching calendars.</p>{/each}
      <section class="hidden-calendars">
        <button type="button" class="hidden-calendars-title" aria-expanded={hiddenListOpen} on:click={() => hiddenListOpen = !hiddenListOpen}>Hidden calendars ({hiddenCalendars.length}) <span aria-hidden="true">{hiddenListOpen ? '▾' : '▸'}</span></button>
        {#if hiddenListOpen}
          {#each hiddenCalendars as calendar}
            <div class="hidden-calendar-choice">
              <i style={`background:${stableCalendarColor(calendar.id)}`} aria-hidden="true"></i>
              <span>{calendar.name}</span>
              <button type="button" class="visibility-toggle" aria-label={`Show ${calendar.name}`} title="Restore to teacher calendars" disabled={visibilityPendingTeacherIds.includes(calendar.teacher_id)} on:click={() => setTeacherVisibility(calendar, false)}><i class="fa-regular fa-eye-slash" aria-hidden="true"></i></button>
            </div>
          {:else}<p class="no-calendars">No matching hidden calendars.</p>{/each}
        {/if}
      </section>
    </aside>

    <section class="calendar-shell">
      <header class="toolbar">
        <button aria-label={`Previous ${view}`} on:click={() => move(-1)}>‹</button><button aria-label={`Next ${view}`} on:click={() => move(1)}>›</button>
        <button on:click={() => { todayKey = calendarDateKey(new Date()); focusDate = new Date(`${todayKey}T12:00:00Z`); loadEvents(); }}>Today</button>
        <button on:click={loadEvents}>Refresh</button>
        <h1>{focusDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' })}</h1>
        {#if selection.kind !== 'none'}<p class="class-total" role="status" aria-label="Class total">{eventsLoading ? 'Loading classes…' : `${displayedClassTotal} ${displayedClassTotal === 1 ? 'class' : 'classes'} this ${view}`}</p>{/if}
        <div class="view-toggle"><button class:active={view === 'month'} on:click={() => { view = 'month'; loadEvents(); }}>Month</button><button class:active={view === 'week'} on:click={() => { view = 'week'; loadEvents(); }}>Week</button></div>
      </header>
      {#if error && !dialogOpen}<div class="error" role="alert">{error}<button aria-label="Dismiss" on:click={() => error = ''}>×</button></div>{/if}
      {#if mutationNotice}<div class="notice" role="status">{mutationNotice}<button aria-label="Dismiss" on:click={() => mutationNotice = ''}>×</button></div>{/if}
      {#if loading}<div class="empty">Loading calendar…</div>
      {:else if selection.kind === 'none'}<div class="empty"><strong>Select a teacher or student</strong><span>No calendars are selected by default. Choose a teacher or student, or use Select all.</span></div>
      {:else}
        <div class="calendar-board">
          <div class="weekday-headings" role="row">
            {#each ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as weekday}
              <span role="columnheader">{weekday}</span>
            {/each}
          </div>
          <div class:week={view === 'week'} class="calendar-grid" style={`--week-count:${visibleDays.length / 7}`}>
            {#each visibleDays as day}
              {#if day.key}
                <div class="day">
                  <button class="day-create-target" aria-label={`Add class on ${day.key}`} title={`Add class on ${day.key}`} on:click={() => openCreateForDay(day)}></button>
                  <span class:today={day.key === todayKey} class="day-number" aria-current={day.key === todayKey ? 'date' : undefined}>{day.date.getUTCDate()}</span>
                  {#if view === 'week' && day.key === todayKey}<div class="current-time" style={`top:${(new Date().getHours() * 60 + new Date().getMinutes()) / 1440 * 100}%`} title="Current time"></div>{/if}
                  {#each eventsFor(events, day) as event}
                    <button class="event" disabled={!options.calendars.some((calendar) => calendar.id === event.calendar_id)} aria-label={`Edit ${event.display_title}`} title={options.calendars.some((calendar) => calendar.id === event.calendar_id) ? 'Edit class' : 'Owning teacher calendar unavailable'} style={`--event:${stableCalendarColor(event.calendar_id)}`} on:click={() => openEdit(event)}>
                      <time>{event.all_day ? 'All day' : calendarTime(event.start)}</time>
                      <span>{event.display_title}</span>
                    </button>
                  {/each}
                </div>
              {:else}<div class="day day-placeholder" aria-hidden="true"></div>{/if}
            {/each}
          </div>
        </div>
      {/if}
      <section class="history">
        <button class="history-title" on:click={() => actionHistoryOpen = !actionHistoryOpen}>Previous actions <span>{actionHistoryOpen ? '▾' : '▸'}</span></button>
        {#if actionHistoryOpen}
          {#each actions as action}
            <div class="history-entry">
              <div class="history-row">
                <button class="history-summary" type="button" aria-expanded={expandedActionId === action.id} aria-label={`Show details for ${action.operation} action from ${new Date(action.created_at).toLocaleString('en-GB')}`} on:click={() => toggleActionDetails(action.id)}>
                  <span><strong>{action.operation}</strong> · {action.scope} · {new Date(action.created_at).toLocaleString('en-GB')}</span>
                  <span class="history-status">{action.status} <span aria-hidden="true">{expandedActionId === action.id ? '▾' : '▸'}</span></span>
                </button>
                {#if action.status === 'reconciling'}<button on:click={() => reconcile(action.id)}>Retry reconciliation</button>{/if}
                {#if action.notifications.some((item) => item.status === 'failed')}<button on:click={() => retry(action.id)}>Resend failed</button>{/if}
              </div>
              {#if expandedActionId === action.id}
                <div class="history-detail" role="region" aria-label="Action details">
                  {#if actionDetailLoadingId === action.id && !actionDetails[action.id]}<p>Loading action details…</p>
                  {:else if actionDetailError?.id === action.id && !actionDetails[action.id]}<p class="history-detail-error" role="alert">Could not load action details: {actionDetailError.message}</p>
                  {:else if actionDetails[action.id]}
                    <h3>{actionDetails[action.id].details.after.title || actionDetails[action.id].details.before.title || 'Class action'}</h3>
                    <p class="history-meta">{actionCalendarName(actionDetails[action.id].details.calendar_id)} · Changed by {actionDetails[action.id].details.actor}</p>
                    {#if actionDetailRows(actionDetails[action.id].details).length}
                      <dl class="history-changes">
                        {#each actionDetailRows(actionDetails[action.id].details) as row}
                          <div><dt>{row.label}</dt><dd>{#if row.before}<span>{row.before}</span><span aria-hidden="true">→</span>{/if}<strong>{row.after}</strong></dd></div>
                        {/each}
                      </dl>
                    {:else if action.operation === 'update'}<p>No class fields changed.</p>{/if}
                    {#if actionDetails[action.id].details.after.responsibility?.enabled}
                      <p><strong>Responsibility incident:</strong> {incidentLabel(actionDetails[action.id].details.after.responsibility)}</p>
                    {/if}
                    {#if actionDetails[action.id].details.reason}<p><strong>Reason:</strong> {actionDetails[action.id].details.reason}</p>{/if}
                    {#if actionDetails[action.id].details.error_message}<p class="history-detail-error"><strong>Action error:</strong> {actionDetails[action.id].details.error_message}</p>{/if}
                    <div class="history-deliveries"><strong>Email notifications</strong>
                      {#if actionDetails[action.id].notifications.length}
                        <ul>{#each actionDetails[action.id].notifications as delivery}<li>{delivery.email} · {delivery.status}{#if delivery.error} · {delivery.error}{/if}</li>{/each}</ul>
                      {:else}<p>None selected.</p>{/if}
                    </div>
                  {/if}
                </div>
              {/if}
            </div>
          {:else}<p>No calendar actions yet.</p>{/each}
        {/if}
      </section>
    </section>
  </div>
</div>

{#if dialogOpen}
  <div class="modal" role="presentation">
    <form class="dialog" role="dialog" aria-modal="true" aria-labelledby="calendar-dialog-title" on:submit|preventDefault={submit} on:input={markDirty}>
      <header><h2 id="calendar-dialog-title">{editingEvent ? 'Edit class' : 'Create class'}</h2><button type="button" aria-label="Close" on:click={closeDialog}>×</button></header>
      <label>Teacher calendar<select bind:value={form.calendar_id} disabled={!!editingEvent} required><option value="">Select calendar</option>{#each editingEvent ? options.calendars.filter((calendar) => calendar.id === editingEvent.calendar_id) : selectedCalendars as calendar}<option value={calendar.id}>{calendar.name}</option>{/each}</select></label>
      <fieldset class="students"><legend>Students and base class price</legend>
        {#each selectedStudents as student}
          <div class="selected-student"><span><strong>{student.name}</strong> <small>Grade {student.grade || '—'}</small></span><label>€ <input class="price" type="number" min="0" step="1" placeholder="Default class price" bind:value={student.basePrice} required /></label><button type="button" aria-label={`Remove ${student.name}`} on:click={() => toggleStudent(student.id)}>×</button></div>
        {/each}
        {#if studentPickerExpanded || !selectedStudents.length}
          <div class="student-picker"><input bind:this={studentSearchInput} type="search" aria-label="Search students" placeholder="Search by name or grade" bind:value={studentSearch} />
            <div class="student-results">{#each availableStudents as student}<div class="student-row"><label><input type="checkbox" on:change={() => toggleStudent(student.id)} />{student.name} <small>Grade {student.grade || '—'}</small></label></div>{:else}<small>No students found.</small>{/each}</div>
          </div>
        {:else}
          <button class="add-student" type="button" on:click={() => { studentPickerExpanded = true; tick().then(() => document.querySelector('[aria-label="Search students"]')?.focus()); }}>Add another student</button>
        {/if}
      </fieldset>
      <label>Class subject<input bind:this={titleInput} list="calendar-subject-options" bind:value={form.subject} on:change={() => { if (form.selectedStudentIds.length >= 2 && !editingEvent) form.groupLetter = options.next_group_letters?.[form.subject] || 'A'; }} placeholder="Search subjects" autocomplete="off" required /><datalist id="calendar-subject-options">{#each options.subjects || [] as subject}<option value={subject.name}></option>{/each}</datalist></label>
      <label>Subject<input class="generated-title" value={generatedTitle} placeholder="Select a subject and student" readonly aria-readonly="true" /></label>
      <label>Google Calendar description<textarea bind:value={form.description} on:change={syncDescription} placeholder="Denis Semionov / Markus Kateščenka / Ivan Michailov" required></textarea><small>Use exact student tokens separated with “ / ”. This controls class enrolment.</small></label>
      {#if form.calendar_id}<p class="meeting">Meeting link: {options.calendars.find((calendar) => calendar.id === form.calendar_id)?.meeting_link || 'No teacher meeting link'}</p>{/if}
      <div class="dates"><div class="date-field"><label for="calendar-start-date">Start date</label><input id="calendar-start-date" type="date" bind:value={form.startDate} required />{#if form.startDate}<small class="selected-weekday" aria-live="polite">{weekdayLabel(form.startDate)}</small>{/if}</div><label>Start time<input type="time" step="300" bind:value={form.startTime} on:input={(event) => { const end = oneHourAfter(event.currentTarget.value); if (end) form.endTime = end; }} required /></label><label>End time<input type="time" step="300" bind:value={form.endTime} required /></label></div>
      {#if form.startTime && form.endTime && form.endTime < form.startTime}<small>Ends the following day.</small>{/if}
      {#if form.calendar_id && form.startDate}<div class="availability" aria-live="polite"><strong>Unavailable times</strong>{#if unavailableTimes.length}<div class="busy-times">{#each unavailableTimes as time}<span>{time.label}</span>{/each}</div>{:else}<small>No existing classes on this day.</small>{/if}</div>{/if}
      {#if !editingEvent}<label class="inline"><input type="checkbox" bind:checked={form.recurring} /> Repeating class</label>{/if}
      {#if form.recurring}<div class="recurrence"><label>Repeat<select bind:value={form.frequency}><option value="daily">Daily</option><option value="weekly">Weekly</option></select></label><label>Interval<select bind:value={form.interval}><option value={1}>Every week/day</option><option value={2}>Bi-weekly</option></select></label><div class="weekdays">{#each ['MO','TU','WE','TH','FR','SA','SU'] as weekday}<label><input type="checkbox" value={weekday} bind:group={form.weekdays} />{weekday}</label>{/each}</div><small>No-end series are saved through August 31 of the active academic year.</small></div>{/if}
      {#if editingEvent && editingEvent.recurring_event_id}<fieldset><legend>Apply to</legend>{#each [['single','This occurrence'],['following','This and following'],['series','Entire series']] as scope}<label class="inline"><input type="radio" bind:group={form.scope} value={scope[0]} />{scope[1]}</label>{/each}{#if form.scope === 'following'}<label class="warning"><input type="checkbox" bind:checked={form.confirm_reset_exceptions} /> I understand future exceptions will be reset</label>{/if}</fieldset>{/if}
      <label>Reason (optional)<textarea bind:value={form.reason}></textarea></label>
      <fieldset><legend>Email notifications (optional)</legend>{#each notificationChoices as choice}<div class="notify"><label><input type="checkbox" disabled={!choice.available} checked={!!selectedNotification(choice)} on:change={() => toggleNotification(choice)} /><span>{choice.label}</span></label>{#if selectedNotification(choice)}<input type="email" aria-label={`Email for ${choice.label}`} value={selectedNotification(choice).email} on:input={(event) => updateNotificationEmail(choice, event.currentTarget.value)} required />{:else}<small>{choice.email || 'No email available'}</small>{/if}</div>{/each}</fieldset>
      {#if editingEvent}<fieldset><legend>Class responsibility (optional, selected occurrence only)</legend><label class="inline"><input type="checkbox" bind:checked={form.responsibilityEnabled} /> Create responsibility incident</label>{#if form.responsibilityEnabled}<select bind:value={form.responsibleParty}><option value="teacher">Teacher</option><option value="student">Student</option></select><select bind:value={form.incidentType}><option value="late">Late</option><option value="cancellation">Cancellation</option><option value="other">Other</option></select><small>The optional reason above will be saved as the incident description.</small>{/if}</fieldset>{/if}
      <footer>{#if editingEvent}<button class="danger" type="button" disabled={submitting} on:click={cancelClass}>Delete class</button>{/if}<span></span><button type="button" on:click={closeDialog}>Cancel</button>{#if error}<div class="error footer-error" role="alert">{error}<button type="button" aria-label="Dismiss" on:click={() => error = ''}>×</button></div>{/if}<button class="primary" aria-busy={submitting} disabled={submitting}>{submitting ? 'Loading…' : editingEvent ? 'Save changes' : 'Create class'}</button></footer>
    </form>
  </div>
{/if}

<style>
  :global(body:has(.calendar-page) main){padding:0;max-width:none;margin:0;width:100%}.calendar-page{min-height:100vh;background:#f8fafc;color:#243047}.workspace{display:grid;grid-template-columns:270px 1fr;min-height:calc(100vh - 64px)}aside{background:white;border-right:1px solid #e5eaf0;padding:22px 16px;box-sizing:border-box;height:calc(100vh - 64px);overflow-y:auto;position:sticky;top:64px}.create{border:0;border-radius:16px;background:#273c75;color:white;padding:14px 22px;font-weight:700;box-shadow:0 2px 8px #1b2a5030;margin-bottom:24px}.aside-heading{display:flex;justify-content:space-between;align-items:center;gap:8px;margin-bottom:10px}.calendar-selection-actions{display:flex;gap:6px;white-space:nowrap}.aside-heading button{border:0;background:none;color:#3657a7;padding:0}.calendar-choice{display:grid;grid-template-columns:20px 12px 1fr;gap:8px;align-items:center;padding:8px 2px}.calendar-choice i{width:10px;height:10px;border-radius:3px}.calendar-choice small{grid-column:3;color:#b06026}.disabled{opacity:.55}.calendar-shell{min-width:0;padding:20px}.toolbar{display:flex;gap:9px;align-items:center;margin-bottom:16px}.toolbar button,.view-toggle button{border:1px solid #dce2e9;background:#fff;border-radius:8px;padding:8px 12px}.toolbar h1{font-size:22px;flex:1;margin:0 12px}.view-toggle .active{background:#e8eefc;color:#203f8a}.calendar-board{overflow-x:auto}.weekday-headings{display:grid;grid-template-columns:repeat(7,minmax(110px,1fr));min-width:770px;background:#fff;border:1px solid #dfe4ea;border-bottom:0}.weekday-headings span{padding:9px 5px;text-align:center;color:#667085;font-size:11px;font-weight:700;letter-spacing:.04em;text-transform:uppercase}.calendar-grid{display:grid;grid-template-columns:repeat(7,minmax(110px,1fr));grid-template-rows:repeat(6,minmax(110px,1fr));min-width:770px;background:#dfe4ea;border:1px solid #dfe4ea;gap:1px}.calendar-grid.week{grid-template-rows:minmax(620px,1fr)}.day{position:relative;background:#fff;min-width:0;padding:5px;overflow:hidden}.current-time{position:absolute;left:0;right:0;border-top:2px solid #d93025;z-index:2}.current-time:before{content:'';position:absolute;width:7px;height:7px;border-radius:50%;background:#d93025;left:0;top:-4px}.outside{background:#f6f7f9;color:#9299a4}.day-number{border:0;background:none;border-radius:50%;width:30px;height:30px}.event{display:flex;width:100%;gap:5px;text-align:left;border:0;border-left:4px solid var(--event);background:color-mix(in srgb,var(--event) 13%,white);border-radius:4px;padding:5px;margin:2px 0;overflow:hidden}.event time{font-size:10px}.event span{white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.empty{height:50vh;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;color:#697386}.error,.notice{padding:10px 14px;margin-bottom:10px;border-radius:8px;display:flex;justify-content:space-between}.error{background:#fff0ee;color:#a72820}.notice{background:#e9f6ee;color:#176538}.error button,.notice button{border:0;background:none}.history{background:white;margin-top:16px;border:1px solid #e3e7ed;border-radius:10px}.history-title{width:100%;text-align:left;border:0;background:none;padding:13px;font-weight:700}.history-row{display:flex;gap:12px;justify-content:space-between;border-top:1px solid #edf0f4;padding:10px 13px;font-size:13px}.modal{position:fixed;inset:0;background:#15213d88;z-index:1000;display:grid;place-items:center;padding:20px}.dialog{background:white;border-radius:16px;padding:20px;width:min(720px,95vw);max-height:92vh;overflow:auto;box-shadow:0 20px 60px #0004}.dialog header,.dialog footer{display:flex;align-items:center;gap:10px}.dialog header h2{flex:1}.dialog header button{border:0;background:none;font-size:25px}.dialog>label,.dialog fieldset{display:block;margin:13px 0}.dialog input,.dialog select,.dialog textarea{box-sizing:border-box;border:1px solid #ccd3dd;border-radius:7px;padding:8px}.dialog>label>select,.dialog>label>textarea,.dialog>label>input{display:block;width:100%;margin-top:5px}.generated-title{background:#f5f7fa;color:#39455a}.student-picker>input{width:100%;margin-bottom:8px}.student-results{max-height:210px;overflow-y:auto;border:1px solid #e2e7ed;border-radius:8px;padding:5px}.selected-student{display:grid;grid-template-columns:1fr auto auto;align-items:center;gap:10px;background:#f5f7fa;border-radius:9px;padding:8px;margin:6px 0}.selected-student button{border:0;background:none;font-size:20px;color:#6c7480}.add-student{border:1px dashed #9ca8b7;background:#fff;border-radius:8px;padding:8px 12px;margin-top:7px;color:#294c9b}.student-row,.notify{display:flex!important;align-items:center;justify-content:space-between;gap:12px;padding:5px}.notify label{display:flex;align-items:center;gap:8px;flex:1}.notify>input{min-width:230px}.notify small{color:#697386}.price{width:110px}.dates{display:grid;grid-template-columns:repeat(3,1fr);gap:8px}.dates label,.date-field,.recurrence label{display:flex;flex-direction:column;gap:4px}.date-field input{width:100%}.selected-weekday{color:#667085;font-weight:600}.inline{display:inline-flex!important;align-items:center;gap:6px;margin-right:15px}.recurrence{display:flex;gap:12px;align-items:end;flex-wrap:wrap}.weekdays{display:flex;gap:5px}.weekdays label{font-size:11px;align-items:center}.warning{display:block;color:#a14d14;margin-top:8px}.dialog footer{border-top:1px solid #e4e8ee;padding-top:15px;flex-wrap:wrap}.dialog footer span{flex:1}.dialog footer button{padding:9px 14px;border:1px solid #ccd3dd;border-radius:7px;background:#fff}.dialog footer .footer-error{margin:0;flex:0 1 300px;padding:8px 10px;font-size:13px}.dialog footer .footer-error button{padding:0;margin-left:8px}.dialog footer .primary{background:#273c75;color:white}.dialog footer .danger{color:#b3261e;border-color:#e6b6b2}@media(max-width:800px){.workspace{grid-template-columns:1fr}aside{border-right:0;height:auto;max-height:42vh;position:static}.calendar-shell{padding:8px}.calendar-grid{grid-template-columns:repeat(7,minmax(80px,1fr));min-width:560px}.weekday-headings{grid-template-columns:repeat(7,minmax(80px,1fr));min-width:560px}.dates{grid-template-columns:1fr}.selected-student{grid-template-columns:1fr auto}.selected-student button{grid-column:2}.notify{align-items:flex-start;flex-direction:column}.notify>input{width:100%;min-width:0}.dialog footer .footer-error{order:10;flex:1 1 100%}}
  .calendar-search{box-sizing:border-box;width:100%;margin:0 0 10px;border:1px solid #ccd3dd;border-radius:8px;padding:8px 10px;font:inherit;font-size:13px;background:#fff}
  .calendar-search:focus{outline:2px solid #9db5ee;outline-offset:1px;border-color:#3657a7}
  .no-calendars{color:#697386;font-size:13px}
  .day-create-target{position:absolute;inset:0;width:100%;height:100%;border:0;background:transparent;cursor:pointer}
  .day-create-target:focus-visible{outline:2px solid #3657a7;outline-offset:-3px}
  .day-number{position:relative;z-index:1;display:inline-grid;place-items:center;pointer-events:none}
  .event{position:relative;z-index:1}
  .event:hover,.event:focus-visible{background:color-mix(in srgb,var(--event) 45%,white);box-shadow:0 3px 9px #203f8a40}
  .event:active{background:color-mix(in srgb,var(--event) 72%,white);box-shadow:inset 0 2px 4px #17203355}
  .availability{display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin:9px 0 13px;padding:9px 11px;border-radius:8px;background:#fff7e8;color:#704600;font-size:13px}
  .busy-times{display:flex;gap:6px;flex-wrap:wrap}
  .busy-times span{padding:3px 7px;border-radius:999px;background:#ffe4b5;font-weight:700}
  .history-row{align-items:center}
  .history-summary{display:flex;align-items:center;justify-content:space-between;gap:15px;flex:1;min-width:0;padding:3px 0;border:0;background:none;text-align:left;color:inherit;cursor:pointer;font:inherit}
  .history-summary:hover,.history-summary:focus-visible{color:#234b9a}
  .history-status{white-space:nowrap;color:#58677c}
  .history-detail{border-top:1px solid #edf0f4;background:#f8faff;padding:15px 18px 17px 24px;font-size:13px;overflow-wrap:anywhere}
  .history-detail h3{font-size:15px;margin:0 0 5px}
  .history-meta{color:#637087;margin:0 0 13px}
  .history-changes{display:grid;gap:0;margin:0 0 12px;border:1px solid #e2e9f2;border-radius:8px;background:#fff}
  .history-changes>div{display:grid;grid-template-columns:minmax(110px,25%) 1fr;gap:12px;padding:8px 11px}
  .history-changes>div+div{border-top:1px solid #edf0f4}
  .history-changes dt{font-weight:700;color:#536174}
  .history-changes dd{margin:0;display:flex;align-items:center;gap:8px;flex-wrap:wrap}
  .history-changes dd>span:first-child{color:#667085;text-decoration:line-through}
  .history-changes dd>strong{font-weight:700;color:#243047}
  .history-deliveries{border-top:1px solid #e2e9f2;margin-top:12px;padding-top:10px}
  .history-deliveries ul{margin:6px 0 0;padding-left:19px}
  .history-deliveries p{margin:5px 0 0;color:#637087}
  .history-detail-error{color:#a72820}
  @media(max-width:800px){.history-changes>div{grid-template-columns:1fr;gap:3px}.history-summary{align-items:flex-start;flex-direction:column;gap:4px}}
  .calendar-choice{display:flex;align-items:flex-start;gap:3px;padding:0}
  .calendar-choice-main{display:grid;grid-template-columns:20px 12px minmax(0,1fr);gap:8px;align-items:center;flex:1;min-width:0;padding:8px 2px;cursor:pointer}
  .calendar-choice-main small{grid-column:3;color:#b06026}
  .calendar-choice-main>span{overflow-wrap:anywhere}
  .visibility-toggle{flex:none;display:grid;place-items:center;width:27px;height:27px;margin-top:5px;border:0;border-radius:7px;background:transparent;color:#667085;cursor:pointer;font-size:14px}
  .visibility-toggle i{width:auto;height:auto;border-radius:0}
  .visibility-toggle:hover,.visibility-toggle:focus-visible{background:#e8eefc;color:#203f8a}
  .visibility-toggle:disabled{opacity:.45;cursor:wait}
  .hidden-calendars{border-top:1px solid #e5eaf0;margin-top:14px;padding-top:5px}
  .hidden-calendars-title{display:flex;justify-content:space-between;align-items:center;width:100%;padding:10px 2px;border:0;background:transparent;color:#526278;font:inherit;font-size:13px;font-weight:700;text-align:left;cursor:pointer}
  .hidden-calendar-choice{display:grid;grid-template-columns:12px minmax(0,1fr) 27px;gap:8px;align-items:center;padding:4px 2px;color:#526278;font-size:13px}
  .hidden-calendar-choice>i{width:10px;height:10px;border-radius:3px}
  .hidden-calendar-choice>span{overflow-wrap:anywhere}
  .hidden-calendar-choice .visibility-toggle{margin-top:0}
  .student-calendars{border-bottom:1px solid #e5eaf0;margin-bottom:16px;padding-bottom:14px}
  .student-calendar-list{max-height:190px;overflow-y:auto}
  .student-calendar-choice{font-size:13px}
  .student-calendar-choice i{width:10px;height:10px;border-radius:3px}
  .aside-heading{flex-wrap:wrap}
  .calendar-selection-actions{flex-wrap:wrap;white-space:normal}
  .class-total{margin:0;color:#405782;font-size:13px;font-weight:700;white-space:nowrap}
  .calendar-grid{grid-template-rows:repeat(var(--week-count),minmax(110px,1fr))}
  .day-placeholder{background:#fff;pointer-events:none}
  .day-number.today{background:#273c75;color:#fff;font-weight:700}
  .event:disabled{cursor:default;opacity:.65}
  @media(max-width:800px){.toolbar{flex-wrap:wrap}.class-total{order:3;width:100%}}
</style>
